import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { MfaToggleCard } from "../../../components/security/MfaToggleCard";
import { signOutToHome } from "../../../lib/auth";
import {
  JUDGE_NOTIFICATION_PREFS_DEFAULTS,
  getJudgeNotificationPrefs,
  updateJudgeNotificationPref,
  type JudgeNotificationPrefKey,
  type JudgeNotificationPrefs,
} from "../../../lib/judgeNotificationPrefs";
import { getMyJudgeProfile, setJudgeAvailability } from "../../../lib/judgeProfile";
import { FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Judge;
const FOREST_MID = THEME.surfaceBg;
const AMBER = THEME.accent;
const SAGE = THEME.success;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

const PAGE_MAX_W = 720;

const NOTIF_ROWS: Array<{ key: JudgeNotificationPrefKey; label: string; description: string }> = [
  {
    key: "email_on_assignment",
    label: "New assignment",
    description: "Email when an admin adds you to a competition.",
  },
  {
    key: "email_on_deadline_24h",
    label: "Deadline reminder",
    description: "Email 24 hours before scoring closes on an assigned competition.",
  },
  {
    key: "email_on_admin_message",
    label: "Direct message from admin",
    description: "Email when an admin sends you a note about an assignment.",
  },
];

export default function JudgeSettingsScreen() {
  const router = useRouter();
  const [available, setAvailable] = useState<boolean>(true);
  const [availLoading, setAvailLoading] = useState(true);
  const [notifs, setNotifs] = useState<JudgeNotificationPrefs>(JUDGE_NOTIFICATION_PREFS_DEFAULTS);
  const [notifsLoading, setNotifsLoading] = useState(true);
  const [notifSaveError, setNotifSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [profile, prefs] = await Promise.all([
        getMyJudgeProfile(),
        getJudgeNotificationPrefs(),
      ]);
      if (cancelled) return;
      setAvailable(profile?.is_available ?? true);
      setAvailLoading(false);
      setNotifs(prefs);
      setNotifsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onToggleAvailability(next: boolean) {
    setAvailable(next);
    const { error } = await setJudgeAvailability(next);
    if (error) {
      // Revert on failure.
      setAvailable(!next);
    }
  }

  // Optimistic update: flip in state immediately, fire the upsert, revert
  // on failure with a brief error toast.
  async function onToggleNotif(key: JudgeNotificationPrefKey, next: boolean) {
    setNotifs((prev) => ({ ...prev, [key]: next }));
    setNotifSaveError(null);
    const { error } = await updateJudgeNotificationPref(key, next);
    if (error) {
      setNotifs((prev) => ({ ...prev, [key]: !next }));
      setNotifSaveError("Couldn't save that preference. Try again?");
      setTimeout(() => setNotifSaveError(null), 3200);
    }
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: PAGE_MAX_W }}>
        {/* Hero */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Judge settings
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.displayLight,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          Settings.
        </Text>

        {/* Sections */}
        <View style={{ marginTop: 28, gap: 22 }}>
          <Section title="Security" subtitle="Sign-in & two-factor">
            <MfaToggleCard theme="dark" />
          </Section>

          <Section title="Email notifications" subtitle="How Coravu reaches you">
            <View
              style={{
                padding: 14,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
                gap: 14,
              }}
            >
              {NOTIF_ROWS.map((row) => (
                <View
                  key={row.key}
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: W }}>
                      {row.label}
                    </Text>
                    <Text
                      style={{
                        marginTop: 2,
                        fontFamily: FONTS.body,
                        fontSize: 11,
                        color: WM,
                      }}
                    >
                      {row.description}
                    </Text>
                  </View>
                  {notifsLoading ? (
                    <ActivityIndicator size="small" color={AMBER} />
                  ) : (
                    <Toggle
                      value={notifs[row.key]}
                      onChange={(v) => void onToggleNotif(row.key, v)}
                    />
                  )}
                </View>
              ))}
              {notifSaveError ? (
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: THEME.destructive }}>
                  {notifSaveError}
                </Text>
              ) : null}
              <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WL, fontStyle: "italic" }}>
                Deadline reminders and direct messages are coming soon — toggles save your preference now, take effect when those flows ship.
              </Text>
            </View>
          </Section>

          <Section title="Availability" subtitle="Are you taking new programs?">
            <View
              style={{
                padding: 14,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: W }}>
                  Available to judge new programs
                </Text>
                <Text
                  style={{
                    marginTop: 2,
                    fontFamily: FONTS.body,
                    fontSize: 11,
                    color: WM,
                  }}
                >
                  When off, you won&apos;t appear in the client&apos;s invite-judge picker.
                  Existing assignments stay assigned.
                </Text>
              </View>
              {availLoading ? (
                <ActivityIndicator size="small" color={AMBER} />
              ) : (
                <Toggle
                  value={available}
                  onChange={(v) => void onToggleAvailability(v)}
                />
              )}
            </View>
          </Section>

          <Section title="Help" subtitle="Tour & guides">
            <View
              style={{
                padding: 14,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
              }}
            >
              <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: WS }}>
                A welcome tour for judges is on the way. For now, your assignments page is
                the home base — that&apos;s where the queue, due dates, and your scoring history live.
              </Text>
            </View>
          </Section>

          <Section title="Sign out" subtitle="">
            <Pressable
              onPress={() => void signOutToHome(router)}
              style={[
                {
                  alignSelf: "flex-start",
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 3,
                  borderWidth: 1,
                  borderColor: BORDER,
                  backgroundColor: "rgba(255,255,255,0.04)",
                } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11.5,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: WS,
                }}
              >
                Sign out
              </Text>
            </Pressable>
          </Section>
        </View>
      </View>
    </ScrollView>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 4,
        }}
      >
        {title}
      </Text>
      {subtitle ? (
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 22,
            lineHeight: 26,
            color: W,
            marginBottom: 14,
          }}
        >
          {subtitle}
        </Text>
      ) : (
        <View style={{ height: 8 }} />
      )}
      {children}
    </View>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <Pressable
      onPress={() => onChange(!value)}
      style={[
        {
          width: 44,
          height: 24,
          borderRadius: 999,
          backgroundColor: value ? "rgba(143,187,138,0.40)" : "rgba(255,255,255,0.10)",
          borderWidth: 1,
          borderColor: value ? SAGE : BORDER,
          padding: 2,
          justifyContent: "center",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View
        style={{
          width: 18,
          height: 18,
          borderRadius: 999,
          backgroundColor: value ? SAGE : WM,
          alignSelf: value ? "flex-end" : "flex-start",
        }}
      />
    </Pressable>
  );
}
